import styles from './RentalPayModal.module.css';

import type { FC } from 'react';
import type { DealChain } from 'src/utils/dealChain';
import type { ApiRentalDealItem } from 'src/types/rental';

import React, { useState } from 'react';
import { Car, Warning, CheckCircle } from '@phosphor-icons/react';

import { useEscrowStash } from 'src/hooks/useEscrowStash';

import { isRentalTerminal } from 'src/utils/rentalActions';
import { rentalPromptPlan } from 'src/utils/rentalPrompts';
import { readRentalContractInfo } from 'src/utils/rentalOnchain';
import { rememberFlow, flowInFlight, FLOW_IN_FLIGHT_MESSAGE } from 'src/utils/escrowFlows';
import {
  payEscrow,
  rentalCreateCall,
  ESCROW_ETH_BALANCE_HINT,
  ESCROW_NETWORK_FEES_NOTE,
} from 'src/utils/escrowCreation';
import {
  rentalToken,
  formatBaseUnits,
  rentalTotalRequired,
  contractRentalInvoice,
  estimateRentalInvoice,
} from 'src/utils/rentalInvoice';

import { useWalletUI, WalletButton } from 'src/blockchain';
import { useFetchRentalDeal, useRentalEscrowCreated, usePrepareRentalPayment } from 'src/queries';

import { Modal } from 'src/components/Modal';
import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';
import { TxProgress, useTxProgress, useClearWhenWalletConnects } from 'src/components/TxProgress';

interface RentalPayModalProps {
  visible: boolean;
  onClose: () => void;
  rental: ApiRentalDealItem;
  /** The deal's network — decides the wallet family, the token approved and the chain switched to. */
  chain: DealChain | null;
  /** Called after the on-chain pay() succeeds so the screen can refetch. */
  onPaid?: () => void;
}

/**
 * Pays the rental escrow (contracts v3, UI §2/§5; api#380 §2.9).
 *
 * Deploy-on-Pay (rental-clock v2.1): a fresh booking has no contract yet, so confirming first asks
 * api for one — `prepare-payment`. The api answers one of two ways. Where the platform deploys the
 * clone (TRON, and Base until the switch), the answer carries the address and the payment is the
 * two prompts it always was: approve, then `pay()`. Where the renter's own wallet creates the clone
 * (Base, api#380), the answer carries a signed **terms sheet** instead, and the payment is three
 * prompts: Create escrow, approve, `pay()`. The dialog cannot know which before the renter presses
 * Pay — the registry's say is not published to the storefront — so until then it promises "two or
 * three" and names the count the moment the api has answered.
 *
 * The sequence — create, remember the hash, report it, approve, pay, and what happens when a step
 * fails or the page was reloaded between two of them — is `payEscrow` (`src/utils/
 * escrowCreation.ts`), pure and unit-tested; this dialog prepares the rental pieces and renders.
 *
 * Both the amount and the token come from the deployed contract, never from this screen: the
 * renter approves the number `pay()` will pull, in the token that clone settles in. That mattered
 * less when every rental was USDT on TRON and a build constant could stand in; on Base the same
 * rental settles in USDC or EURC by the listing's currency, and approving a build constant would
 * approve a token that does not exist on the deal's chain.
 */
export const RentalPayModal: FC<RentalPayModalProps> = ({
  visible,
  onClose,
  rental,
  chain,
  onPaid,
}) => {
  const walletUI = useWalletUI(chain?.family);
  const preparePayment = usePrepareRentalPayment();
  const { reportEscrowCreated } = useRentalEscrowCreated();
  const fetchRental = useFetchRentalDeal();
  const tx = useTxProgress();

  useClearWhenWalletConnects(tx, chain?.family);

  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [success, setSuccess] = useState(false);
  // Set before anything async runs. `tx.isRunning` only turns true once the wallet is reached, and
  // the deploy plus the contract read before it take seconds — seconds in which the button would
  // still accept a second press and start a second approve and a second `pay()`.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The deal as prepare-payment returned it (with the deployed contract, or the terms sheet); the
  // prop stays the pre-deploy snapshot until the screen behind refetches.
  const [prepared, setPrepared] = useState<ApiRentalDealItem | null>(null);

  const effective = prepared ?? rental;
  const state = effective.rentalContractState;
  const token = rentalToken(rental, chain);
  const contractAddress = state?.address ?? effective.contractAddress ?? '';
  const isEvm = chain?.family === 'evm';
  // A creation transaction this browser sent for the booking and never saw reported (api#380).
  const stashedCreation = useEscrowStash(rental.id, contractAddress);
  // What the dialog promised when the run started, kept until it ends (see DeliveryPayModal).
  const [runThreePrompts, setRunThreePrompts] = useState<boolean | null>(null);

  // The renter's wallet creates the contract (api#380): a signed sheet and no contract yet. Known
  // once prepare-payment has answered, or when this browser already sent the creation.
  const needsEscrow = !contractAddress && !!effective.escrowTerms;
  // What the dialog promises and runs (api#380, PR-5): three prompts by the sheet, the stash or the
  // chain registry saying `buyer`; a hedge only against an api that does not say (`rentalPrompts`).
  const { threePrompts, promisedThree, promptsUnknown, preparingLabel } = rentalPromptPlan({
    isEvm,
    contractAddress,
    runThreePrompts,
    hasSheet: needsEscrow,
    hasStashedCreation: !contractAddress && !!stashedCreation,
    escrowCreation: chain?.escrowCreation,
  });

  // Invoice lines from the on-chain mirror once deployed (`rentalTotalRequired()` is the same
  // deposit + rental + fee sum the contract's `getTotalRequired()` returns); before the deploy,
  // from the deal's own amounts through the same integer math the contract applies at
  // `initialize()`, so the estimate and the on-chain ask agree by construction.
  const invoice =
    state != null
      ? contractRentalInvoice(state)
      : estimateRentalInvoice({
          rentalAmount: effective.rentalAmount,
          depositAmount: effective.depositAmount,
          decimals: token.decimals,
        });
  const format = (value: bigint) => `${formatBaseUnits(value, token.decimals)} ${token.symbol}`;
  const totalDisplay = format(rentalTotalRequired(invoice));
  const isProcessing = (isSubmitting || tx.isRunning) && !success;

  const handleClose = () => {
    if (isProcessing) {
      return;
    }
    setError('');
    setNotice('');
    setSuccess(false);
    setRunThreePrompts(null);
    tx.reset();
    onClose();
  };

  /** The booking as the payment flow reads it: the address, the sheet, and whether it still takes a payment. */
  const viewOf = (item: ApiRentalDealItem) => ({
    contractAddress: item.rentalContractState?.address ?? item.contractAddress ?? null,
    escrowTerms: item.escrowTerms,
    payable: !isRentalTerminal(item.status) && !item.rentalContractState?.isPaid,
  });

  const handleConfirm = async () => {
    if (isProcessing) {
      return;
    }

    // A run of this deal is still at its prompt in the wallet, or waiting on the network — maybe
    // from a dialog that has since been closed: no second run until it settles.
    if (flowInFlight(rental.id)) {
      setError(FLOW_IN_FLIGHT_MESSAGE);

      return;
    }

    setError('');
    setNotice('');
    setIsSubmitting(true);

    try {
      let deal: ApiRentalDealItem = effective;

      // Deploy-on-Pay: no contract yet — ask api for one: the platform's deploy, or the terms sheet
      // for this wallet to create it from. Idempotent on the api side.
      if (!contractAddress || state == null) {
        deal = await preparePayment.mutateAsync(rental.id);
        setPrepared(deal);
      }

      const view = viewOf(deal);

      // Known only now, from the api's answer: the count the dialog promises for this run.
      setRunThreePrompts(!view.contractAddress && (!!view.escrowTerms || !!stashedCreation));

      const paid = await tx.run((onProgress, signal) => {
        const flow = payEscrow({
          dealId: rental.id,
          chainId: chain?.chainId,
          deal: view,
          createCall: rentalCreateCall,
          walletUI,
          report: async (txHash) => viewOf(await reportEscrowCreated(rental.id, txHash)),
          refresh: async () => viewOf(await fetchRental(rental.id)),
          readContract: (address) => readRentalContractInfo(walletUI, address, chain?.chainId),
          paySignature: 'pay()',
          onProgress,
          onNotice: setNotice,
          signal,
        });

        // Remembered, outside this dialog, until it settles — however the race in `tx.run` ends.
        rememberFlow(rental.id, flow);

        return flow;
      }, 'Transaction failed. Please try again.');

      if (paid) {
        setSuccess(true);
        onPaid?.();
      }
      // Otherwise the run stopped; whatever is stashed is what the next press continues from, and
      // the dialog reads it afresh (`useEscrowStash`).
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Transaction failed. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
      // The promise of this run ends with it; the next run reads the deal afresh.
      setRunThreePrompts(null);
    }
  };

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      icon={
        success ? (
          <CheckCircle size={64} color="white" weight="fill" />
        ) : (
          <Car size={64} color="white" weight="fill" />
        )
      }
      header={success ? 'Payment sent' : 'Pay for this rental'}
      buttonText={
        success
          ? 'Close'
          : // The deploy is its own visible phase (UI §2): a renter who pressed pay and got a
            // silent button for several seconds is a renter who presses it again.
            preparePayment.isPending
            ? preparingLabel
            : isProcessing
              ? 'Processing…'
              : 'Approve & pay'
      }
      onSubmit={success ? handleClose : handleConfirm}
      disabled={isProcessing}
    >
      <Stack gap={3} width="100%">
        {!success ? (
          <>
            <Stack gap={1} className={styles.info}>
              <Row label="Vehicle" value={`${rental.listing.make.name} ${rental.listing.model.name}`} />
              <Row label="Rental" value={format(invoice.rental)} />
              <Row label="Deposit (refundable)" value={format(invoice.deposit)} />
              <Row label="Platform fee (9%, non-refundable)" value={format(invoice.fee)} />
              <Row label="Total to pay" value={totalDisplay} />
              {promisedThree && isEvm ? (
                <Typography color="lightWhite" variant="small">
                  {ESCROW_NETWORK_FEES_NOTE}
                </Typography>
              ) : null}
            </Stack>

            <Stack gap={1} className={styles.warning}>
              <Stack flexDirection="row" gap={1} alignItems="center">
                <Warning size={20} color="white" weight="fill" />
                <Typography color="white" fontWeight="600">
                  {promisedThree
                    ? 'Create escrow, approve, pay'
                    : `Approve ${totalDisplay} in your wallet`}
                </Typography>
              </Stack>
              <Typography color="lightWhite" variant="small">
                {promisedThree ? (
                  <>
                    Three wallet prompts will appear: first your wallet creates the escrow contract
                    for this order (a small network fee), then an{' '}
                    <b>approve of exactly {totalDisplay}</b>, then the payment. The deposit is held
                    in escrow and returned when you complete the rental; the platform fee is
                    non-refundable.
                    {isEvm ? ` ${ESCROW_ETH_BALANCE_HINT}` : ''}
                  </>
                ) : promptsUnknown ? (
                  <>
                    Two or three wallet prompts will appear: your wallet may first create the escrow
                    contract for this rental (a small network fee), then an{' '}
                    <b>approve of exactly {totalDisplay}</b> — this is the contract amount, not a
                    gas fee, so do not lower it — then the payment. The deposit is held in escrow
                    and returned when you complete the rental; the platform fee is non-refundable.
                    You need a small ETH balance on Base for the transactions.
                  </>
                ) : (
                  <>
                    Two prompts will appear: first your wallet asks you to{' '}
                    <b>approve exactly {totalDisplay}</b> — this is the contract amount, not a gas
                    fee, so do not lower it. Then confirm the payment. The deposit is held in escrow
                    and returned when you complete the rental; the platform fee is non-refundable.
                    {isEvm
                      ? ' You need a small ETH balance on this network to pay for the two transactions.'
                      : ''}
                  </>
                )}
              </Typography>
            </Stack>

            {stashedCreation && !isProcessing ? (
              <Typography color="white" variant="small" className={styles.warning}>
                An escrow creation was already sent for this rental (tx {stashedCreation}). Approve
                &amp; pay reports it first — the escrow is not created again.
              </Typography>
            ) : null}
          </>
        ) : (
          <Stack gap={1} className={styles.info}>
            <Typography color="white" fontWeight="600">
              Payment confirmed. The rental status will update shortly.
            </Typography>
          </Stack>
        )}

        <TxProgress
          state={tx.state}
          done={success}
          error={tx.error}
          // No wallet to sign with: `Connect Wallet` and the connection window stand in for Retry (app#315).
          connect={tx.needsWallet && chain ? <WalletButton family={chain.family} /> : null}
          explorerTxUrl={chain?.explorerTxUrl}
          onRetry={() => {
            tx.reset();
            handleConfirm();
          }}
        />

        {notice && isProcessing ? (
          <Typography color="lightWhite" variant="small">
            {notice}
          </Typography>
        ) : null}

        {error ? (
          <Typography color="white" variant="small" className={styles.error}>
            {error}
          </Typography>
        ) : null}
      </Stack>
    </Modal>
  );
};

const Row: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack flexDirection="row" justifyContent="space-between" gap={2}>
    <Typography color="lightWhite" variant="small">
      {label}
    </Typography>
    <Typography color="white" variant="small">
      {value}
    </Typography>
  </Stack>
);
